import { useMutation, UseMutationOptions } from 'react-query';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  ShareTemplateResponse,
  AddPresetsResponse,
} from 'src/types/TShare';
import {
  createTemplate,
  updateTemplate,
  deleteTemplate,
  setDefaultTemplate,
  addPresetTemplates,
} from '../functions/templateApi';

/**
 * Mutation hook for creating a new template
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 * (handles eventual consistency across backend servers)
 */
export function useCreateTemplateMutation(
  mutationOptions?: UseMutationOptions<ShareTemplateResponse, FetchError, CreateTemplateRequest>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (data: CreateTemplateRequest) => {
    const result = await createTemplate(accessToken, data);
    if ('error' in result) {
      throw result;
    }
    return result as ShareTemplateResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for updating an existing template
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useUpdateTemplateMutation(
  templateId: string,
  mutationOptions?: UseMutationOptions<ShareTemplateResponse, FetchError, UpdateTemplateRequest>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (data: UpdateTemplateRequest) => {
    const result = await updateTemplate(accessToken, templateId, data);
    if ('error' in result) {
      throw result;
    }
    return result as ShareTemplateResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for deleting a template
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useDeleteTemplateMutation(
  mutationOptions?: UseMutationOptions<any, FetchError, string>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (templateId: string) => {
    const result = await deleteTemplate(accessToken, templateId);
    if ('error' in result) {
      throw result;
    }
    return result;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for setting a template as default
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useSetDefaultTemplateMutation(
  mutationOptions?: UseMutationOptions<ShareTemplateResponse, FetchError, string>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (templateId: string) => {
    const result = await setDefaultTemplate(accessToken, templateId);
    if ('error' in result) {
      throw result;
    }
    return result as ShareTemplateResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

/**
 * Mutation hook for adding preset templates to user's account
 * Note: Does not auto-invalidate queries to allow for delayed refetch
 */
export function useAddPresetsMutation(
  mutationOptions?: UseMutationOptions<AddPresetsResponse, FetchError, string[]>,
) {
  const accessToken = useAccessToken();

  const mutationFn = async (presetIds: string[]) => {
    const result = await addPresetTemplates(accessToken, presetIds);
    if ('error' in result) {
      throw result;
    }
    return result as AddPresetsResponse;
  };

  return useMutation(mutationFn, mutationOptions);
}

